/**
 * React Query hooks for logs operations.
 * Provides declarative data fetching for self-service debugging.
 *
 * @module hooks/useLogs
 */

import { useQuery } from '@tanstack/react-query';
import { logsService } from '@/services';

/**
 * Query key factory for logs.
 * Ensures consistent cache key generation.
 */
export const logsKeys = {
  all: ['logs'] as const,
  traceEvents: () => [...logsKeys.all, 'traceEvents'] as const,
  traceEventsDetail: (traceId: string) => [...logsKeys.traceEvents(), traceId] as const,
};

/**
 * Hook to fetch all log events for a specific trace ID
 *
 * @param traceId - The trace ID to query
 * @example
 * const { data: traceEvents, isLoading, error } = useTraceEvents('abc123');
 */
export function useTraceEvents(traceId: string) {
  return useQuery({
    queryKey: logsKeys.traceEventsDetail(traceId),
    queryFn: () => logsService.getTraceEvents(traceId),
    enabled: !!traceId,
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}
